import React, { useState } from 'react';
import { Mail, DollarSign, Star, Twitter, Linkedin, Facebook, Instagram, Globe, Edit, Check, X } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import ProfilePictureUpload from '@/components/profile/ProfilePictureUpload';
import { useToast } from "@/hooks/use-toast";
import { Journalist } from '@/hooks/journalist/types';

interface ProfileHeaderProps {
  journalist: Journalist;
  isOwnProfile: boolean;
  userProfile: any;
  onAvatarUpdate: (url: string) => Promise<void>;
  isFollowing: boolean;
  toggleFollow: () => void;
  updateProfile: (updates: Partial<Journalist>) => Promise<void>;
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  journalist,
  isOwnProfile,
  userProfile,
  onAvatarUpdate,
  isFollowing,
  toggleFollow,
  updateProfile
}) => {
  const { toast } = useToast();
  const [editMode, setEditMode] = useState(false);
  const [name, setName] = useState(journalist.name);
  const [role, setRole] = useState(journalist.role);
  const [bio, setBio] = useState(journalist.bio);
  const [editTags, setEditTags] = useState<string[]>(journalist.expertise || []);
  const [newTag, setNewTag] = useState('');
  
  const handleContact = () => {
    toast({
      description: "Contact form opened",
    });
  };
  
  const handleSendTip = () => {
    toast({
      title: "Tip Sent",
      description: `Thank you for supporting ${journalist.name}'s work!`,
    });
  };
  
  const handleSubscribe = () => {
    toast({
      title: "Subscription Started",
      description: `You are now subscribed to ${journalist.name}'s content.`,
    });
  };
  
  const saveChanges = async () => {
    await updateProfile({
      name,
      role,
      bio,
      expertise: editTags
    });
    setEditMode(false);
  };
  
  const cancelChanges = () => {
    setName(journalist.name);
    setRole(journalist.role);
    setBio(journalist.bio);
    setEditTags(journalist.expertise || []);
    setEditMode(false);
  };
  
  const addTag = () => {
    if (newTag.trim() && !editTags.includes(newTag.trim())) {
      setEditTags([...editTags, newTag.trim()]);
      setNewTag('');
    }
  };
  
  const removeTag = (tagToRemove: string) => {
    setEditTags(editTags.filter(tag => tag !== tagToRemove));
  };
  
  return (
    <>
      <div 
        className="h-64 w-full bg-cover bg-center relative" 
        style={{ 
          backgroundImage: `url(${journalist.banner})`,
          backgroundColor: journalist.brandColor
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/70"></div>
      </div>
      
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-shrink-0">
            {isOwnProfile ? (
              <ProfilePictureUpload
                avatarUrl={userProfile?.avatar_url || journalist.avatar}
                onUploadComplete={onAvatarUpdate}
                size="lg"
              />
            ) : (
              <Avatar className="h-32 w-32 border-4 border-white dark:border-gray-800 shadow-md">
                <AvatarImage src={journalist.avatar} alt={journalist.name} />
                <AvatarFallback>{journalist.name.substring(0, 2)}</AvatarFallback>
              </Avatar>
            )}
          </div>
          
          <div className="flex-grow">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
              <div>
                {editMode ? (
                  <div className="space-y-2">
                    <Input 
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="text-2xl font-bold"
                    />
                    <Input 
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      className="text-base"
                    />
                  </div>
                ) : (
                  <>
                    <h1 className="text-3xl font-bold text-foreground">{journalist.name}</h1>
                    <p className="text-lg text-muted-foreground">{journalist.role}</p>
                  </>
                )}
              </div>
              
              <div className="flex gap-2 mt-4 md:mt-0">
                {isOwnProfile ? (
                  editMode ? (
                    <>
                      <Button variant="outline" size="sm" onClick={cancelChanges}>
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                      <Button size="sm" onClick={saveChanges}>
                        <Check className="h-4 w-4 mr-2" />
                        Save
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Profile
                    </Button>
                  )
                ) : (
                  <>
                    <Button variant="outline" size="sm" onClick={handleContact}>
                      <Mail className="h-4 w-4 mr-2" />
                      Contact
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleSendTip}>
                      <DollarSign className="h-4 w-4 mr-2" />
                      Send Tip
                    </Button>
                    <Button 
                      size="sm" 
                      className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                      onClick={isFollowing ? toggleFollow : handleSubscribe}
                    >
                      <Star className="h-4 w-4 mr-2" />
                      {isFollowing ? "Following" : "Subscribe"}
                    </Button>
                  </>
                )}
              </div>
            </div>
            
            {editMode ? (
              <Textarea 
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                className="mb-6"
                rows={3}
              />
            ) : (
              <p className="text-foreground mb-6">{journalist.bio}</p>
            )}
            
            <div className="flex flex-wrap gap-2 mb-6">
              {editMode ? (
                <>
                  {editTags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                      {tag}
                      <X className="h-3 w-3 cursor-pointer" onClick={() => removeTag(tag)} />
                    </Badge>
                  ))}
                  <div className="flex">
                    <Input 
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="Add expertise..."
                      className="text-sm h-8 w-32"
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    />
                    <Button variant="outline" size="sm" className="ml-1 h-8" onClick={addTag}>Add</Button>
                  </div>
                </>
              ) : (
                journalist.expertise.map((topic: string) => (
                  <Badge key={topic} variant="secondary">{topic}</Badge>
                ))
              )}
            </div>
            
            <div className="flex items-center gap-4 text-muted-foreground">
              <a href={`https://twitter.com/${journalist.social.twitter}`} target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
                <Twitter className="h-5 w-5" />
              </a>
              <a href={`https://linkedin.com/in/${journalist.social.linkedin}`} target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
                <Linkedin className="h-5 w-5" />
              </a>
              <a href={`https://facebook.com/${journalist.social.facebook}`} target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
                <Facebook className="h-5 w-5" />
              </a>
              <a href={`https://instagram.com/${journalist.social.instagram}`} target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
                <Instagram className="h-5 w-5" />
              </a>
              <a href={`https://${journalist.social.website}`} target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
                <Globe className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfileHeader;
